import React, { useEffect, useState } from "react";
import {
  Button,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "@nextui-org/react";
import { FaCopy } from "react-icons/fa6";
import {
  UpdateOneData,
  getDataWithFilterUrl,
} from "../../../controllers/APIController";
import { StandardCancelButton } from "../../../components/StandardButtons";

/**
 * ApiEditModal Component
 * Modal for editing existing API keys
 * Allows users to update API key names while maintaining security
 * @param {Object} props - Component props
 * @param {Function} props.onSubmitHandler - Function to call after successful submission
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onOpen - Function to open the modal
 * @param {Function} props.onOpenChange - Function to handle modal open state changes
 * @param {string} props.id - ID of the API key to edit
 * @returns {JSX.Element} API key edit modal
 */
const ApiEditModal = ({
  onSubmitHandler,
  isOpen,
  onOpen,
  onOpenChange,
  id,
}) => {
  // Component state
  const [keyName, setKeyName] = useState("");
  const [apiKeyValue, setApiKeyValue] = useState("");
  const [fieldError, setFieldError] = useState(null);

  // User and organization data from session storage
  const userLogged = JSON.parse(sessionStorage.getItem("userData"));
  const orgInfo = userLogged && userLogged.user.LastOrgId;
  const orgFilterUrl = "filters[Organization][id][$eqi]=" + orgInfo;

  /**
   * Copy API key to clipboard
   * Uses navigator.clipboard API to copy the masked key
   */
  const handleCopyClick = () => {
    navigator.clipboard
      .writeText(apiKeyValue)
      .then(() => {
        // Successfully copied
      })
      .catch((error) => {
        console.error("Failed to copy text:", error);
      });
  };

  /**
   * Submit API key update
   * Validates input and sends update to API
   * @param {Function} onClose - Function to close the modal
   */
  const handleSubmit = async (onClose) => {
    if (keyName === "") {
      setFieldError("Name field is required!");
      return;
    }

    const payloadData = {
      name: keyName,
    };

    try {
      await UpdateOneData("keys", id, payloadData);
      onSubmitHandler();
      onClose();
    } catch (error) {
      console.error("Error updating API key:", error);
    }
  };

  /**
   * Handle keyboard events for form submission
   * Allows submission on Enter key press
   * @param {KeyboardEvent} event - Keyboard event object
   * @param {Function} onClose - Function to close the modal
   */
  const handleKeyDown = (event, onClose) => {
    if (event.key === "Enter") {
      event.preventDefault(); // Prevent default form submission
      handleSubmit(onClose);
    }
  };

  /**
   * Load existing API key data on component mount
   * Fetches current key information and sets form state
   */
  useEffect(() => {
    const responseMetricUrl = orgFilterUrl + "&filters[id][$eqi]=" + id;
    
    getDataWithFilterUrl("keys", responseMetricUrl)
      .then((data) => {
        setKeyName(data?.data[0]?.attributes?.name);
        setApiKeyValue("*************************"); // Masked for security
      })
      .catch((error) => {
        console.error("Error fetching API key data:", error);
      });
  }, [orgFilterUrl, id]);

  return (
    <Modal isOpen={isOpen} onOpenChange={onOpenChange} size="2xl">
      <ModalContent>
        {(onClose) => (
          <div
            onKeyDown={(event) => handleKeyDown(event, onClose)}
            tabIndex="0"
            className="outline-none"
          >
            {/* Modal Header */}
            <ModalHeader className="flex flex-col gap-1">
              Edit API Key
            </ModalHeader>
            
            {/* Modal Body */}
            <ModalBody>
              <div>
                {/* API Key Name Input */}
                <Input
                  type="text"
                  className="mb-12"
                  isRequired
                  label="Name"
                  placeholder=" "
                  variant="faded"
                  labelPlacement="outside"
                  value={keyName || ""}
                  onChange={(event) => {
                    setKeyName(event.target.value);
                  }}
                  isInvalid={fieldError}
                  errorMessage={fieldError}
                />
                
                {/* Masked API Key Display */}
                <Input
                  type="text"
                  className="mb-8 mt-4"
                  isRequired
                  label="API Key"
                  endContent={
                    <FaCopy
                      onClick={handleCopyClick}
                      className="cursor-pointer"
                    />
                  }
                  placeholder=" "
                  variant="faded"
                  labelPlacement="outside"
                  value={apiKeyValue || ""}
                  readOnly
                />
              </div>
            </ModalBody>
            
            {/* Modal Footer */}
            <ModalFooter>
              {/* Cancel Button */}
              <StandardCancelButton onPress={onClose}>
                Cancel
              </StandardCancelButton>
              
              {/* Update Button */}
              <Button
                color="danger"
                onClick={() => handleSubmit(onClose)}
                onKeyDown={(event) => handleKeyDown(event, onClose)}
              >
                Update
              </Button>
            </ModalFooter>
          </div>
        )}
      </ModalContent>
    </Modal>
  );
};

export default ApiEditModal;
